// ─── Il canale in tempo reale, dalla parte del browser ──────────────────────
//
// ⭐ L'EVENTO INVALIDA, NON SOSTITUISCE (§ 03.3). Arrivato il segnale, si
// invalida la query di quel giorno e TanStack Query rilegge. Applicare la
// modifica a mano sullo stato locale vorrebbe dire tenere due copie del
// calendario — cioè rifare la macchina che si sta smontando.
//
// ⚖️ E IL SOCKET NON È LA FONTE DELLA VERITÀ. Se cade, la pagina resta
// corretta: si riattacca con attesa crescente e nel frattempo la griglia si
// rilegge a intervalli. Oggi il difetto più fastidioso del gestionale vecchio
// nasce proprio da un socket stantio che nessuno sostituiva.

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { gettoneCorrente } from '../../lib/api.js';

interface Evento {
  type: string;
  entity: string;
  id: string;
  date: string;
  at: string;
}

/**
 * Apre il canale e invalida il giorno toccato. Torna `true` finché il canale è
 * attaccato: chi lo usa può dirlo a schermo, e può leggere più spesso quando
 * non lo è.
 */
export function usaLive(): boolean {
  const qc = useQueryClient();
  const [attaccato, setAttaccato] = useState(false);

  useEffect(() => {
    let socket: WebSocket | null = null;
    let riattacco: ReturnType<typeof setTimeout> | null = null;
    let attesa = 1000;
    let smesso = false;

    function attacca(): void {
      if (smesso) return;
      const gettone = gettoneCorrente();
      // Senza gettone non si prova nemmeno: aprire e farsi chiudere sarebbe un
      // giro di riattacchi che non può riuscire finché non si rifà l'accesso.
      if (!gettone) {
        riattacco = setTimeout(attacca, 2000);
        return;
      }

      const protocollo = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(
        `${protocollo}//${window.location.host}/api/v1/live?token=${encodeURIComponent(gettone)}`,
      );

      socket.onopen = () => {
        attesa = 1000;
        setAttaccato(true);
      };
      socket.onmessage = (m) => {
        try {
          const e = JSON.parse(String(m.data)) as Evento;
          if (e.entity !== 'booking' || !e.date) return;
          void qc.invalidateQueries({ queryKey: ['calendario', e.date] });
          void qc.invalidateQueries({ queryKey: ['prenotazione', e.id] });
        } catch {
          // Un messaggio che non si sa leggere non è un motivo per cadere.
        }
      };
      socket.onclose = () => {
        setAttaccato(false);
        if (smesso) return;
        riattacco = setTimeout(attacca, attesa);
        attesa = Math.min(attesa * 2, 30_000);
      };
      // `onerror` non serve: a un errore segue sempre `onclose`, e riattaccare
      // in tutti e due i posti farebbe partire due socket.
    }

    attacca();
    return () => {
      smesso = true;
      if (riattacco) clearTimeout(riattacco);
      socket?.close();
    };
  }, [qc]);

  return attaccato;
}
